import { ConfirmDialog } from '@components/ConfirmDialog';
import { DownloadLinkButton } from '@components/Download/DownloadLinkButton';
import { getFileExtension } from '@components/Icons/FileIcon';
import { DisableableTooltip } from '@components/Tooltips/DisableableTooltip';
import { ProjectDocumentsContext } from '@modules/folders/components/ProjectDocumentsContextProvider';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { PermissionsModal } from '@modules/securityGroups/components/PermissionsModal/PermissionsModal';
import { Delete, Draw, Edit, EditOff, Share } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { Alert, Divider, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import { useGetMe } from '@utils/useGetMe';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import {
  DocumentFragmentFragment, ShareableResourceType, SignatureStatus, useCancelSignatureRequestMutation, 
  useFolderEntriesQuery, useFolderEntryQuery, useGetDocumentQuery, useProjectDocumentsQuery, useProjectTasksQuery, useRemoveDocumentMutation 
} from 'gql/index';
import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import React from 'react';
import { SignatureDialog } from './DocumentSignatureDialog';

import { RequestSignatureDialog } from './RequestSignatureDialog/RequestSignatureDialog';
import { RenameDocumentDialog } from './RenameDocumentDialog';



interface Props {
  document: DocumentFragmentFragment;
  renameAction?: () => void;
}

export const DocumentActions: React.FC<Props> = ({ document, renameAction }) => {
  const { formatMessage } = useIntl();
  const invalidateQuery = useQueryInvalidator();
  const [isConfirmRemoveDialogOpen, setIsConfirmRemoveDialogOpen] = useState(false);
  const { notifySuccess } = useNotification();
  const { me } = useGetMe();
  const { canManageDocuments, canManageDocumentPublicLinkSharing } = useCurrentProject();
  const isPdfDocument = getFileExtension(document.fileName).toLocaleLowerCase() == 'pdf';
  const currentUserCanSign = !document?.isSigned && document?.signatoryCustomerId === me?.id;

  const { projectId, isArchived, isTemplate } = useCurrentProject();
  const { setSelectedEntryId, selectedEntryId } = useContext(ProjectDocumentsContext);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [isAddingRoleAssignment, setIsAddingRoleAssignment] = useState(false);
  const [isSignatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [openAskSignatureDialog, setOpenAskSignatureDialog] = useState(false);

  const isSignaturePending = document?.signatureRequest
    ? document?.signatureRequest?.signatureStatus != SignatureStatus.Signed
    : (document.requiresSignatures && !document?.isSigned);

  const deleteDocumentMutation = useRemoveDocumentMutation({
    onSuccess: () => {
      notifySuccess(formatMessage({ id: 'Document deleted' }));
      invalidateQuery(useProjectDocumentsQuery, { projectId });
      if (document.folderEntry?.folderId) {
        invalidateQuery(useFolderEntriesQuery, { projectId, folderId: document.folderEntry.folderId });
      }
      invalidateQuery(useProjectTasksQuery, { projectId });
      setIsConfirmRemoveDialogOpen(false);
    }
  });

  const deleteDocument = async () => {
    if (!document) return;

    setSelectedEntryId?.(undefined);

    await deleteDocumentMutation.mutateAsync({ input: { documentId: document.id, projectId } });
  };

  const onRenameClicked = () => {
    if (renameAction) {
      renameAction();
    } else {
      setRenameDialogOpen(true);
    }
  };

  const { mutate: cancelSignature } = useCancelSignatureRequestMutation({
    onSuccess: () => {
      notifySuccess(formatMessage({ id: 'Signature request cancelled successfully.' }));
      invalidateQuery(useProjectDocumentsQuery, { projectId });
      if (document.folderEntry?.folderId) {
        invalidateQuery(useFolderEntriesQuery, { projectId, folderId: document.folderEntry.folderId });
      }
      invalidateQuery(useGetDocumentQuery, { id: document.id });
      if (selectedEntryId) invalidateQuery(useFolderEntryQuery, { id: selectedEntryId });
      invalidateQuery(useProjectTasksQuery, { projectId });
    }
  });

  const handleCancelSignatureRequest = () => {
    if (document)
      cancelSignature({ input: { projectId: projectId, documentId: document.id } });
  };

  return (
    <>
      <DownloadLinkButton
        variant='MenuItem'
        fileName={document?.fileName || ''}
        link={`/${getTenantIdentifier()}/api/Documents/${document?.id}`}
      />

      {currentUserCanSign && (
        <MenuItem disabled={isArchived} onClick={() => setSignatureDialogOpen(true)}>
          <ListItemIcon><Draw /></ListItemIcon>

          <ListItemText primary={formatMessage({ id: 'Sign now' })} />
        </MenuItem>
      )}

      {(canManageDocumentPublicLinkSharing) && 
      <>
        <MenuItem onClick={() => setIsAddingRoleAssignment(true)}>
          <ListItemIcon><Share /></ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'Share' })} />
        </MenuItem>
        {document.publicSlug &&
        <MenuItem
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/${getTenantIdentifier()}/shared/${document.publicSlug}`);
            notifySuccess(formatMessage({ id: 'Copied to clipboard' }));
          }}
        >
          <ListItemIcon><LinkIcon /></ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'Copy public access URL' })} />
        </MenuItem>
        }
      </>
      }

      {canManageDocuments && !isTemplate && isSignaturePending &&
      <MenuItem onClick={handleCancelSignatureRequest} disabled={isArchived}>
        <ListItemIcon><EditOff /></ListItemIcon>

        <ListItemText primary={formatMessage({ id: 'Cancel signature request' })} />
      </MenuItem>
      }

      {canManageDocuments && !isTemplate && !isSignaturePending && !document?.isSigned &&
      <DisableableTooltip disabled={isPdfDocument} title={formatMessage({ id: 'Only pdf documents can be signed.' })}>
        <MenuItem onClick={() => isPdfDocument && setOpenAskSignatureDialog(true)} disabled={!isPdfDocument || isArchived}>
          <ListItemIcon><Draw /></ListItemIcon>

          <ListItemText primary={formatMessage({ id: 'Request signature' })} />
        </MenuItem>
      </DisableableTooltip>
      }

      {canManageDocuments && <Divider />}

      {canManageDocuments &&
      <MenuItem onClick={onRenameClicked}>
        <ListItemIcon><Edit /></ListItemIcon>
        <ListItemText primary={formatMessage({ id: 'Rename' })} />
      </MenuItem>}

      {canManageDocuments && 
      <MenuItem onClick={() => setIsConfirmRemoveDialogOpen(true)} disabled={isArchived}>
        <ListItemIcon><Delete /></ListItemIcon>
        <ListItemText primary={formatMessage({ id: 'Delete' })} />
      </MenuItem>}


      <PermissionsModal open={isAddingRoleAssignment} resource={({ resourceType: ShareableResourceType.Document, ...document })} onClose={() => setIsAddingRoleAssignment(false)} />

      <ConfirmDialog
        open={isConfirmRemoveDialogOpen && document != null}
        onCancel={() => setIsConfirmRemoveDialogOpen(false)}
        onConfirm={deleteDocument}
        title={formatMessage({ id: 'Remove from project?' })}
        content={
          <>
            {formatMessage({ id: '{fileName} will be permanently deleted.' }, { fileName: document?.fileName })}

            {document.formValueId && (
              <Alert severity='warning'>
                {formatMessage({ id: 'This document was uploaded by a form. All permissions will be removed, and the document will no longer be visible in the documents view, but it may still be seen in the form submission.' })}
              </Alert>
            )}
          </>}
        confirmText={formatMessage({ id: 'Remove' })}
        confirmColor='error'
        loading={deleteDocumentMutation.isPending}
      />

      <RenameDocumentDialog document={document} open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} />

      <SignatureDialog
        documentId={document.id}
        projectId={document.projectId}
        actionId={document.zohoActionId ?? ''}
        requestId={document.zohoRequestId ?? ''}
        open={isSignatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
      />
{document.id && document.folderEntry?.folderId && (
      <RequestSignatureDialog
        documentId={document.id}
        projectId={document.projectId}
        open={openAskSignatureDialog}
        onClose={() => setOpenAskSignatureDialog(false)}
      />
      )}
    </>);
};